import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Calendar, CreditCard, Mail } from 'lucide-react';
import { getDonation } from '@/lib/api/donations';
import { formatCurrency } from '@/lib/utils/currency';
import { Card, LoadingSpinner, Alert, Button } from '@/components/ui';
import type { Currency } from '@/types/donation';

function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const donationId = searchParams.get('id');

  // Redirect to home if no donation ID
  useEffect(() => {
    if (!donationId) {
      navigate('/');
    }
  }, [donationId, navigate]);

  // Fetch donation details
  const { data, isLoading, error } = useQuery({
    queryKey: ['donation', donationId],
    queryFn: () => {
      if (!donationId) {
        throw new Error('Donation ID is required');
      }
      return getDonation(donationId);
    },
    enabled: !!donationId,
    retry: 2,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-screen">
        <Card className="max-w-md mx-auto">
          <LoadingSpinner message="Loading donation details..." />
        </Card>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-screen">
        <Card className="max-w-md mx-auto">
          <Alert variant="error" title="Unable to Load Details">
            {error?.message || 'Could not retrieve donation details. Please check your email for confirmation.'}
          </Alert>
          <Button onClick={() => navigate('/')} className="w-full mt-4">
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  const donation = data.data;
  const formattedAmount = formatCurrency(donation.amount, donation.currency as Currency);
  const donationDate = new Date(donation.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const isRecurring = donation.recurringSchedule !== 'one_time';

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen flex items-center justify-center">
      <div className="max-w-2xl w-full">
        <Card className="backdrop-blur-sm bg-dark-card/90 border-gold/30 shadow-2xl overflow-hidden p-8 sm:p-10">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/20 blur-2xl rounded-full"></div>
              <div className="relative inline-flex items-center justify-center w-24 h-24 bg-dark-lighter border-2 border-green-500/50 rounded-full shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                <CheckCircle className="w-14 h-14 text-green-500" />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Thank You for <span className="text-gold">Your Donation!</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Your generous contribution has been successfully processed and will help shape the programming of tomorrow.
            </p>
          </div>

          {/* Donation Details */}
          <div className="bg-white rounded-xl p-6 mb-8 text-left shadow-inner">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-gold rounded-full"></span>
              Donation Details
            </h2>

            <div className="grid grid-cols-1 gap-6">
              {/* Amount and Date row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Amount</div>
                    <div className="text-2xl font-medium text-gray-900 leading-none">{formattedAmount}</div>
                    {isRecurring && (
                      <div className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase rounded mt-2 border border-blue-100">
                        {donation.recurringSchedule === 'monthly' ? 'Monthly' : 'Yearly'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Date & Time</div>
                    <div className="text-md font-medium text-gray-900">{donationDate}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notices */}
          <div className="space-y-4 mb-10">
            {/* Receipt Alert */}
            <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-left">
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-500/20 rounded-full">
                <Mail className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-sm text-blue-200">
                <span className="font-bold text-blue-100">Receipt sent!</span> An email has been sent to your inbox.
              </p>
            </div>

            {/* Recurring Alert */}
            {isRecurring && (
              <div className="flex items-start gap-3 p-4 bg-gold/10 border border-gold/20 rounded-xl text-left">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gold/20 rounded-full mt-0.5">
                  <CheckCircle className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gold mb-1">Recurring Donation Active</p>
                  <p className="text-xs text-gold/80 leading-relaxed">
                    You'll be charged <span className="font-bold text-gold">{formattedAmount}</span> {donation.recurringSchedule === 'monthly' ? 'every month' : 'every year'}.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/')}
              className="flex-1 bg-gold hover:bg-gold-light text-dark font-black py-4 h-auto shadow-lg shadow-gold/20"
            >
              MAKE ANOTHER DONATION
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="flex-1 border-gold/50 text-gold hover:bg-gold/10 font-black py-4 h-auto"
            >
              PRINT RECEIPT
            </Button>
          </div>

          {/* Footer message */}
          <div className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed italic">
              "Your support helps us continue our important work. Thank you for making a difference!"
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default SuccessPage;
